import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { AutoComplete } from "@/components/AutoCompleteInput";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { useActiveOrganization } from "@/hooks/organizations";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";
import { getActiveOrganizationMembers, useActiveOrgMembers } from "@/queries/organization-members";
import { useCurrentUser } from "@/queries/users";
import { useGetWorkspaceInvites } from "@/queries/workspace";
import { WorkspaceInviteFormValues } from "@/schemas/workspace-invite";
import { WorkspaceMember } from "@/types/workspaceMembers";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader } from "@/utils/headers";

const COMMON_EMAIL_DOMAINS = [
  "@gmail.com",
  "@outlook.com",
  "@yahoo.com",
  "@hotmail.com",
  "@icloud.com",
  "@protonmail.com",
  "@aol.com",
  "@mail.com",
];

interface SelectEmailsProps {
  disabled?: boolean;
  email?: string | null;
  organizationId?: number;
  label?: string;
  required?: boolean;
}

const SelectEmails = ({
  disabled = false,
  email,
  organizationId,
  label = "Emails",
  required = true,
}: SelectEmailsProps) => {
  const form = useFormContext<WorkspaceInviteFormValues>();
  const watchedEmails = form.watch("emails");
  const watchedWorkspaceIds = form.watch("workspace_ids");
  const initializedRef = useRef(false);
  const [inputQuery, setInputQuery] = useState("");
  const userToken = useUserToken();
  const { data: activeOrganization } = useActiveOrganization();
  const { data: user } = useCurrentUser();
  const { subscription } = useOrganizationSubscriptionData();

  const effectiveOrgId = organizationId ?? activeOrganization?.id;

  const { data: orgMembersData, isLoading: isLoadingMembers } = useActiveOrgMembers({
    organization_id: effectiveOrgId ?? undefined,
    perPage: 250,
    page: 1,
  });

  const { data: totalWorkspaceInvites, isLoading: loadingWorkspaceInvites } =
    useGetWorkspaceInvites({
      organization_id: effectiveOrgId ?? 0,
      status: "pending",
    });

  const workspaceMembersByWorkspace = useQueries({
    queries: (watchedWorkspaceIds ?? []).map(workspaceId => ({
      queryKey: [ENDPOINTS.legacy_workspace_members, userToken, workspaceId],
      queryFn: async () => {
        const url = new URL(ENDPOINTS.legacy_workspace_members);
        url.searchParams.set("workspace_id", workspaceId.toString());
        return fetch(url, { headers: authHeader(userToken) }).then(handleResponse);
      },
      enabled: !!userToken && !!workspaceId,
    })),
  });

  const setValue = useCallback(
    (values: string[]) => {
      form.setValue("emails", values, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.setValue],
  );

  useEffect(() => {
    if (email && !initializedRef.current) {
      initializedRef.current = true;
      setValue([email]);
    }
  }, [email, setValue]);

  const maxUsers = subscription?.max_users === null ? "Unlimited" : (subscription?.max_users ?? 5);
  const totalCurrentMembers = getActiveOrganizationMembers(orgMembersData?.items).length;
  const pendingInvitesCount = totalWorkspaceInvites?.items?.length ?? 0;
  const emailsCount = watchedEmails?.length ?? 0;

  const availableSeats =
    typeof maxUsers === "number" && maxUsers > 0
      ? Math.max(0, maxUsers - totalCurrentMembers - pendingInvitesCount - emailsCount)
      : "Unlimited";

  const canAddMoreEmails = typeof availableSeats === "number" ? availableSeats > 0 : true;

  const invitedEmails = useMemo(() => {
    const pendingInvites = totalWorkspaceInvites?.items ?? [];
    const selectedWorkspaceIds = new Set(watchedWorkspaceIds ?? []);
    return new Set(
      pendingInvites
        .filter(inv => selectedWorkspaceIds.has(inv.workspace_id))
        .map(inv => inv.invitee_email.toLowerCase()),
    );
  }, [totalWorkspaceInvites?.items, watchedWorkspaceIds]);

  const membersInAllWorkspaces = useMemo(() => {
    const workspaceIds = watchedWorkspaceIds ?? [];
    if (workspaceIds.length === 0) {
      return new Set<string>();
    }

    const workspaceMembersByWorkspaceData = workspaceMembersByWorkspace
      .map(q => q.data)
      .filter(Boolean) as { workspace_members: WorkspaceMember[] }[];

    if (workspaceMembersByWorkspaceData.length !== workspaceIds.length) {
      return new Set<string>();
    }

    const firstWorkspaceMembers = new Set(
      (workspaceMembersByWorkspaceData[0]?.workspace_members ?? [])
        .map(m => m.email?.toLowerCase())
        .filter(Boolean),
    );

    return workspaceMembersByWorkspaceData.reduce((acc, { workspace_members }) => {
      const memberEmails = new Set(
        workspace_members.map(m => m.email?.toLowerCase()).filter(Boolean),
      );
      return new Set([...acc].filter(e => memberEmails.has(e)));
    }, firstWorkspaceMembers);
  }, [workspaceMembersByWorkspace, watchedWorkspaceIds]);

  const hasReachedLimit =
    !canAddMoreEmails &&
    typeof availableSeats === "number" &&
    !isLoadingMembers &&
    !loadingWorkspaceInvites;

  const currentUserEmailLower = user?.email?.toLowerCase() ?? "";

  const availableMembers = useMemo(() => {
    const members = getActiveOrganizationMembers(orgMembersData?.items);
    return members.filter(
      member =>
        member.user.email.toLowerCase() !== currentUserEmailLower &&
        !membersInAllWorkspaces.has(member.user.email.toLowerCase()) &&
        !invitedEmails.has(member.user.email.toLowerCase()),
    );
  }, [orgMembersData?.items, membersInAllWorkspaces, invitedEmails, currentUserEmailLower]);

  const emailSuggestions = useMemo(() => {
    const query = inputQuery;
    if (!query || !query.includes("@") || query.includes(".")) {
      return [];
    }
    const domainFromEnteredEmails = (watchedEmails ?? [])
      .map(e => {
        const domain = e.split("@")[1];
        return domain ? `@${domain}` : null;
      })
      .filter((d): d is string => !!d);
    const userDomain = user?.email?.split("@")[1];
    const currentUserEmailDomain = userDomain ? `@${userDomain}` : "";
    const domains = [
      ...domainFromEnteredEmails,
      currentUserEmailDomain,
      ...COMMON_EMAIL_DOMAINS,
    ].filter(Boolean);
    const uniqueDomains = [...new Set(domains)];
    const [localPart, domainPart] = query.split("@");
    if (!domainPart) {
      return uniqueDomains.map(domain => localPart + domain);
    }
    return uniqueDomains
      .filter(domain => domain.toLowerCase().startsWith("@" + domainPart.toLowerCase()))
      .map(domain => localPart + domain);
  }, [inputQuery, watchedEmails, user?.email]);

  const pendingInviteEmails = useMemo(() => {
    const invites = totalWorkspaceInvites?.items ?? [];
    const orgMemberEmails = new Set(
      getActiveOrganizationMembers(orgMembersData?.items).map(m => m.user.email.toLowerCase()),
    );
    const unique = new Map<string, string>();
    invites.forEach(inv => {
      const inviteeEmail = inv.invitee_email.toLowerCase();
      if (!orgMemberEmails.has(inviteeEmail)) {
        unique.set(inviteeEmail, inv.invitee_email);
      }
    });
    return Array.from(unique.values());
  }, [totalWorkspaceInvites?.items, orgMembersData?.items]);

  const memberOptions = useMemo(() => {
    const orgMemberOptions = availableMembers.map(member => ({
      id: member.user.email,
      value: member.user.email,
      label: member.user.email,
    }));
    const pendingInviteOptions = pendingInviteEmails
      .filter(
        inviteeEmail =>
          inviteeEmail.toLowerCase() !== currentUserEmailLower &&
          !invitedEmails.has(inviteeEmail.toLowerCase()),
      )
      .map(inviteeEmail => ({
        id: inviteeEmail,
        value: inviteeEmail,
        label: inviteeEmail,
      }));
    const base = [...orgMemberOptions, ...pendingInviteOptions];
    let options = base;
    if (email && email.toLowerCase() !== currentUserEmailLower && !base.some(o => o.id === email)) {
      options = [{ id: email, value: email, label: email }, ...base];
    }
    const suggestionOptions = emailSuggestions
      .slice(0, 5)
      .filter(s => s.toLowerCase() !== currentUserEmailLower && !invitedEmails.has(s.toLowerCase()))
      .map(s => ({ id: s, value: s, label: s }));
    return [...suggestionOptions, ...options];
  }, [
    availableMembers,
    pendingInviteEmails,
    email,
    emailSuggestions,
    invitedEmails,
    currentUserEmailLower,
  ]);

  const selectedValues = useMemo(() => [...(watchedEmails ?? [])], [watchedEmails]);

  const optionsWhenAtLimit = useMemo(() => {
    const orgMemberOptions = availableMembers.map(member => ({
      id: member.user.email,
      value: member.user.email,
      label: member.user.email,
    }));
    const pendingInviteOptions = pendingInviteEmails
      .filter(
        inviteeEmail =>
          inviteeEmail.toLowerCase() !== currentUserEmailLower &&
          !invitedEmails.has(inviteeEmail.toLowerCase()),
      )
      .map(inviteeEmail => ({
        id: inviteeEmail,
        value: inviteeEmail,
        label: inviteeEmail,
      }));
    return [...orgMemberOptions, ...pendingInviteOptions];
  }, [availableMembers, pendingInviteEmails, invitedEmails, currentUserEmailLower]);

  const handleSelectionChange = useCallback(
    (values: string[]) => {
      const filtered = currentUserEmailLower
        ? values.filter(v => v.toLowerCase() !== currentUserEmailLower)
        : values;
      const seen = new Set<string>();
      const deduped = filtered.filter(v => {
        const key = v.toLowerCase();
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
      setValue(deduped);
    },
    [setValue, currentUserEmailLower],
  );

  const showSeatLimit =
    typeof maxUsers === "number" && maxUsers > 0 && !isLoadingMembers && !loadingWorkspaceInvites;

  return (
    <FormField
      control={form.control}
      name="emails"
      render={() => (
        <FormItem className="flex w-full flex-col">
          <FormLabel className="flex items-center justify-between gap-2">
            <div>
              {label} {required && <span className="text-red-500">*</span>}
            </div>
            {showSeatLimit && (
              <span className="inline-flex items-center text-sm font-normal text-gray-500">
                Available seats: {canAddMoreEmails ? availableSeats : 0}/{maxUsers}
              </span>
            )}
          </FormLabel>
          <FormControl>
            <AutoComplete
              multi
              options={hasReachedLimit ? optionsWhenAtLimit : memberOptions}
              selectedValues={selectedValues}
              onSelectionChange={handleSelectionChange}
              placeholder={
                hasReachedLimit
                  ? "Add existing members (upgrade to add new users)"
                  : "Search members or enter email..."
              }
              addNewOption={!hasReachedLimit}
              maxVisibleTags={1}
              isDisabled={disabled || !!email}
              getAddOptionText={query => `Add "${query}"`}
              onInputChange={setInputQuery}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default SelectEmails;
